import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  Mail,
  Key,
  Lock,
  Loader2,
  ArrowRight,
  Eye,
  EyeOff,
  KeyRound,
  PlayCircle,
  Quote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SetPasswordModal from "@/components/SetPasswordModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_isaac-platform/artifacts/9qsxumik_Sterile%20Mate%20Logo%20%282%29.jpg";
const TEAM_PHOTO = "/api/static/email_assets/login_diverse_team.jpg";

export default function HospitalLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  // Capture ?next=/marketplace/... redirect target on mount and stash it so the
  // existing "redirect_after_login" flow (see login-success handler below) will
  // route users back to whatever page they were on when they clicked Hospital Login.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      try { sessionStorage.setItem("redirect_after_login", next); } catch (_) { /* ignore */ }
    }
  }, [location.search]);

  const [loginMethod, setLoginMethod] = useState("password"); // "password" or "code"
  const [showPassword, setShowPassword] = useState(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    access_code: ""
  });

  // Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetStep, setResetStep] = useState(1); // 1: email, 2: code + new password
  const [resetData, setResetData] = useState({
    access_code: "",
    new_password: "",
    confirm_password: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error("Please enter your email");
      return;
    }

    if (loginMethod === "password" && !formData.password) {
      toast.error("Please enter your password");
      return;
    }

    if (loginMethod === "code" && !formData.access_code) {
      toast.error("Please enter your access code");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        ...(loginMethod === "password" 
          ? { password: formData.password } 
          : { access_code: formData.access_code }
        )
      };

      const response = await axios.post(`${API}/health-systems/login`, payload);
      
      if (response.data.token) {
        // Check if user needs to set password (first time OTP login)
        if (response.data.needs_password_setup) {
          setTempToken(response.data.token);
          setShowSetPasswordModal(true);
          toast.info("Please set a permanent password for your account");
          return;
        }

        // Store auth data
        localStorage.setItem("hospital_token", response.data.token);
        localStorage.setItem("hospital_user", JSON.stringify(response.data.user));
        localStorage.setItem("hospital_health_system", JSON.stringify(response.data.health_system));
        
        toast.success(`Welcome, ${response.data.user.name}!`);
        
        // Check for redirect URL
        const redirectUrl = sessionStorage.getItem("redirect_after_login");
        if (redirectUrl) {
          sessionStorage.removeItem("redirect_after_login");
          navigate(redirectUrl);
        } else {
          navigate("/hospital/portal");
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please check your credentials.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSetSuccess = () => {
    // After password is set, complete the login
    if (tempToken) {
      localStorage.setItem("hospital_token", tempToken);
      
      // Decode token to get user info (simple decode, not verification)
      try {
        const payload = JSON.parse(atob(tempToken.split('.')[1]));
        const userData = { id: payload.sub, email: payload.email };
        localStorage.setItem("hospital_user", JSON.stringify(userData));
      } catch (e) {
        console.error("Error decoding token:", e);
      }
      
      toast.success("Password set! You're now logged in.");
      navigate("/hospital/portal");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (resetStep === 1) {
      // Send recovery code
      setLoading(true);
      try {
        const response = await axios.post(`${API}/hospital/forgot-password`, {
          email: forgotPasswordEmail
        });
        
        toast.success("If this email exists, a recovery code has been sent.");
        
        // In development, show the code
        if (response.data.dev_code) {
          toast.info(`Dev code: ${response.data.dev_code}`, { duration: 10000 });
        }
        
        setResetStep(2);
      } catch (error) {
        toast.error("An error occurred. Please try again.");
      } finally {
        setLoading(false);
      }
    } else {
      // Reset password with code
      if (resetData.new_password !== resetData.confirm_password) {
        toast.error("Passwords do not match");
        return;
      }
      
      if (resetData.new_password.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }

      setLoading(true);
      try {
        await axios.post(`${API}/hospital/reset-password`, {
          email: forgotPasswordEmail,
          access_code: resetData.access_code,
          new_password: resetData.new_password
        });
        
        toast.success("Password reset successfully! You can now login.");
        setShowForgotPassword(false);
        setResetStep(1);
        setResetData({ access_code: "", new_password: "", confirm_password: "" });
        setLoginMethod("password");
      } catch (error) {
        const message = error.response?.data?.detail || "Failed to reset password";
        toast.error(message);
      } finally {
        setLoading(false);
      }
    }
  };

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="Ask Isaac" className="h-14 mx-auto mb-4" />
            </Link>
            <h1 className="text-2xl font-bold text-white">Reset Password</h1>
            <p className="text-slate-400 mt-2">
              {resetStep === 1 ? "Enter your email to receive a recovery code" : "Enter the code and your new password"}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <form onSubmit={handleForgotPassword} className="p-6 space-y-5">
              {resetStep === 1 ? (
                <div>
                  <Label className="flex items-center gap-1">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="you@yourorganization.org"
                    className="mt-1 h-11 text-base"
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                  />
                </div>
              ) : (
                <>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Key className="w-4 h-4 text-slate-400" />
                      Recovery Code
                    </Label>
                    <Input
                      type="text"
                      value={resetData.access_code}
                      onChange={(e) => setResetData({...resetData, access_code: e.target.value.toUpperCase()})}
                      placeholder="XXXXXXXX"
                      className="mt-1 font-mono tracking-wider uppercase"
                      maxLength={8}
                      required
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Lock className="w-4 h-4 text-slate-400" />
                      New Password
                    </Label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={resetData.new_password}
                      onChange={(e) => setResetData({...resetData, new_password: e.target.value})}
                      placeholder="••••••••"
                      className="mt-1 h-11 text-base"
                      autoComplete="new-password"
                      required
                      minLength={8}
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Lock className="w-4 h-4 text-slate-400" />
                      Confirm Password
                    </Label>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={resetData.confirm_password}
                      onChange={(e) => setResetData({...resetData, confirm_password: e.target.value})}
                      placeholder="••••••••"
                      className="mt-1 h-11 text-base"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </>
              )}

              <Button 
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Processing...</>
                ) : resetStep === 1 ? (
                  <>Send Recovery Code <ArrowRight className="w-4 h-4 ml-2" /></>
                ) : (
                  <>Reset Password <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </form>

            <div className="px-6 pb-6 pt-2 border-t border-slate-100">
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetStep(1);
                }}
                className="w-full text-center text-sm text-slate-500 hover:text-blue-600"
              >
                ← Back to login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="hospital-login-page">
      {/* ============ LEFT — HERO PHOTO + WELCOME ============ */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Diverse clinical team photograph */}
        <img
          src={TEAM_PHOTO}
          alt="Diverse clinical team at Sterile Mate partner hospital"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        {/* Warm blue overlay so white text stays legible over the photo */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1a35]/70 via-[#0a1a35]/45 to-[#0056A8]/60" />
        {/* Content stacked over the photo */}
        <div className="relative z-10 flex flex-col justify-between p-10 xl:p-14 w-full text-white">
          <div>
            <Link to="/" className="inline-flex items-center gap-3">
              <img src={LOGO_URL} alt="Sterile Mate" className="h-12 w-auto drop-shadow-xl" />
              <div>
                <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-cyan-200">
                  Powered by AskIsaac
                </p>
                <p className="text-sm text-white/80">Healthcare Facility Management</p>
              </div>
            </Link>
          </div>

          <div className="max-w-lg">
            <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
              Welcome back
            </p>
            <h2 className="text-4xl xl:text-5xl font-black leading-[1.05] mb-5 drop-shadow-xl">
              One command center for every corridor, every code.
            </h2>
            <p className="text-lg text-white/90 leading-relaxed drop-shadow max-w-md mb-6">
              Sign in to place orders, dispatch service calls, track backorders, and coordinate your
              supply chain, EVS, sterile processing, and facilities teams — all in one place.
            </p>

            {/* 90-sec tour CTA */}
            <Link
              to="/how-it-works"
              className="inline-flex items-center gap-2.5 px-5 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur-md text-white text-sm font-semibold transition-colors group"
              data-testid="login-tour-cta"
            >
              <PlayCircle className="w-5 h-5 text-cyan-300 group-hover:scale-110 transition-transform" />
              Watch the 90-second tour
            </Link>

            {/* Testimonial quote */}
            <figure className="mt-8 border-l-2 border-cyan-400/60 pl-4 max-w-md">
              <Quote className="w-4 h-4 text-cyan-300 mb-1" />
              <blockquote className="text-sm text-white/95 leading-relaxed italic drop-shadow">
                &ldquo;Sterile Mate rescued three cases last month. Their team found allocated SKUs
                nobody else could get their hands on — and had them on-site the next morning.&rdquo;
              </blockquote>
              <figcaption className="text-xs text-cyan-200 mt-2 not-italic">
                — Supply Chain Director · Level-1 Trauma Center, Texas Medical Center
              </figcaption>
            </figure>
          </div>

          <div className="flex items-center gap-6 text-xs text-white/70">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              HIPAA-aligned
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              AAMI ST108 Ready
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              OIG · SAM.gov · OFAC
            </span>
          </div>
        </div>
      </div>

      {/* ============ RIGHT — LOGIN FORM ============ */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          {/* Mobile-only header (photo panel is hidden on small screens) */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="Sterile Mate" className="h-12 mx-auto mb-3" />
            </Link>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-cyan-700">
              Powered by AskIsaac
            </p>
          </div>

          <div>
            <h1 className="text-3xl font-black text-slate-900 mb-2">Hospital Staff Login</h1>
            <p className="text-slate-600">
              Access your health system portal — orders, dispatch, and analytics in one place.
            </p>
          </div>

          {/* Login Card */}
          <div className="mt-8 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Health System Portal</p>
                  <p className="text-sm text-slate-500">For registered hospital staff</p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <Tabs value={loginMethod} onValueChange={setLoginMethod} className="mb-5">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="password" className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Password
                  </TabsTrigger>
                  <TabsTrigger value="code" className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4" />
                    Access Code
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <Label className="flex items-center gap-1">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="you@yourorganization.org"
                    className="mt-1 h-11 text-base"
                    autoFocus
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-testid="hospital-login-email"
                  />
                </div>

                {loginMethod === "password" ? (
                  <div>
                    <Label className="flex items-center gap-1">
                      <Lock className="w-4 h-4 text-slate-400" />
                      Password
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="••••••••"
                        className="pr-12 h-11 text-base"
                        autoComplete="current-password"
                        data-testid="hospital-login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-400 hover:text-slate-600"
                        data-testid="hospital-login-password-toggle"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:underline mt-2 py-1"
                    >
                      Forgot password?
                    </button>
                  </div>
                ) : (
                  <div>
                    <Label className="flex items-center gap-1">
                      <Key className="w-4 h-4 text-slate-400" />
                      Access Code
                    </Label>
                    <Input
                      type="text"
                      value={formData.access_code}
                      onChange={(e) => setFormData({...formData, access_code: e.target.value.toUpperCase()})}
                      placeholder="XXXXXXXX"
                      className="mt-1 font-mono tracking-wider uppercase h-11 text-base"
                      maxLength={8}
                      autoComplete="one-time-code"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      data-testid="hospital-login-code"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      8-character code provided during registration
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 h-11 text-sm font-semibold"
                  data-testid="hospital-login-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Signing In...</>
                  ) : (
                    <>Sign In <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </div>

            {/* First-time user callout */}
            <div className="px-6 pb-5 pt-1">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1.5">
                  First time here?
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  If your facility has already activated with Sterile Mate, use the{" "}
                  <span className="font-semibold">Access Code</span> tab and enter the 8-character code
                  from your onboarding email. Otherwise,{" "}
                  <Link to="/hospital/self-register" className="font-semibold text-blue-700 hover:underline" data-testid="hospital-register-link">
                    register your facility here
                  </Link>{" "}
                  — it takes about 90 seconds and gives you instant access to the marketplace,
                  backorder sourcing, and dispatch tools.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-6 space-y-2">
            <Link to="/login" className="text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1">
              ← Back to main login
            </Link>
            <p className="text-slate-500 text-sm">
              Need help?{" "}
              <a href="mailto:info@sterilemate.com" className="text-blue-700 hover:underline font-medium" data-testid="hospital-login-support-email">
                Contact Support
              </a>
            </p>
          </div>

          {/* Social proof strip — hospital categories served */}
          <div className="mt-10 pt-6 border-t border-slate-200" data-testid="login-social-proof">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 text-center mb-4">
              Trusted across the healthcare continuum
            </p>
            <div className="grid grid-cols-2 gap-3 text-center">
              {[
                { count: "208+", label: "Hospitals & Health Systems" },
                { count: "24 / 7", label: "Emergency Dispatch" },
                { count: "5+ yrs", label: "Serving Texas Medical Center" },
                { count: "2,000+", label: "Surgical SKUs Stocked" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                  <p className="text-lg font-black text-slate-900 leading-none">{s.count}</p>
                  <p className="text-[10px] font-medium text-slate-500 mt-1 uppercase tracking-wider leading-tight">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-4 italic">
              Level-1 Trauma · Academic Medical · Ambulatory Surgery · Rural Critical Access
            </p>
          </div>
        </div>
      </div>

      {/* Set Password Modal */}
      <SetPasswordModal
        open={showSetPasswordModal}
        onClose={() => setShowSetPasswordModal(false)}
        onSuccess={handlePasswordSetSuccess}
        token={tempToken}
      />
    </div>
  );
}
